import * as React from 'react'
import classNames from 'classnames'
import { CircleCheck, CircleX } from 'lucide-react'
import { useTranslation } from 'translations'
import { ReadinessCheck } from '../../model/readiness'
import './readinessChecklist.scss'

interface ReadinessChecklistProps {
    checks: ReadinessCheck[]
    /** Called when the user clicks the fix link of a failing check, with the check key to scroll to. */
    onFix: (key: ReadinessCheck['key']) => void
}

/**
 * The list of checks a listing must pass before it can be switched on. A
 * passing list collapses to one line; a failing one names each fix.
 */
export function ReadinessChecklist({ checks, onFix }: ReadinessChecklistProps) {
    const { t } = useTranslation()
    const failing = checks.filter((check) => !check.ok)

    if (failing.length === 0) {
        return (
            <p className="readiness-summary">
                <CircleCheck size={16} className="readiness-mark ok" aria-hidden="true" />
                {t('channels.googleTTD.readiness.allPass', { count: checks.length })}
            </p>
        )
    }

    return (
        <ul className="readiness-list" aria-label={t('channels.googleTTD.readiness.title')}>
            {checks.map((check) => (
                <li key={check.key} className={classNames('readiness-item', { ok: check.ok })}>
                    {check.ok ? (
                        <CircleCheck size={16} className="readiness-mark ok" aria-hidden="true" />
                    ) : (
                        <CircleX size={16} className="readiness-mark fail" aria-hidden="true" />
                    )}
                    <span className="readiness-text">
                        {!check.ok && check.detail
                            ? t(`channels.googleTTD.readiness.${check.key}Failing`, {
                                  detail: check.detail,
                                  defaultValue: t(`channels.googleTTD.readiness.${check.key}`, {
                                      defaultValue: check.key,
                                  }),
                              })
                            : t(`channels.googleTTD.readiness.${check.key}`, { defaultValue: check.key })}
                    </span>
                    {!check.ok && (
                        <button type="button" className="readiness-fix" onClick={() => onFix(check.key)}>
                            {t(`channels.googleTTD.readiness.fix.${check.key}`)}
                        </button>
                    )}
                </li>
            ))}
        </ul>
    )
}
